import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { ArrowRight, Code2, Search, Megaphone, Check } from "lucide-react";

const HERO_IMGS = [
  "https://customer-assets.emergentagent.com/job_marketing-quote-1/artifacts/z42ps7ff_IMG_0193.jpeg",
  "https://customer-assets.emergentagent.com/job_marketing-quote-1/artifacts/8zsnqqyv_IMG_0194.jpeg",
];
const WEBDEV_IMG = "https://images.unsplash.com/photo-1526887593587-a307ea5d46b4";
const SEO_IMG = "https://images.unsplash.com/photo-1551288049-bebda4e38f71";
const SMM_IMG = "https://images.pexels.com/photos/12673815/pexels-photo-12673815.jpeg";

function HeroSlideshow() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % HERO_IMGS.length), 4500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative w-full h-[360px] overflow-hidden" data-testid="hero-slideshow">
      <AnimatePresence>
        <motion.img
          key={i}
          src={HERO_IMGS[i]}
          alt="Brand Bloom clients"
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 0.9, ease: "easeInOut" }, scale: { duration: 5, ease: "easeOut" } }}
        />
      </AnimatePresence>
      <div className="absolute bottom-3 right-3 z-10 flex gap-1.5" aria-hidden>
        {HERO_IMGS.map((_, idx) => (
          <span key={idx} className={`h-1.5 w-1.5 rounded-full transition-colors ${idx === i ? "bg-white" : "bg-white/40"}`} />
        ))}
      </div>
    </div>
  );
}

const services = [
  { icon: Code2, title: "Website Development", desc: "Custom, conversion-focused sites & web apps engineered for speed.", img: WEBDEV_IMG, tag: "Build" },
  { icon: Search, title: "SEO", desc: "Technical SEO, content & backlinks that compound traffic over time.", img: SEO_IMG, tag: "Rank" },
  { icon: Megaphone, title: "Social Media Management", desc: "Content, reels & campaigns that build an audience that buys.", img: SMM_IMG, tag: "Grow" },
];

function AnimatedServiceCard({ s, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.12 }}
      className="group bg-white border border-zinc-200 hover:shadow-xl transition-shadow duration-300"
    >
      {/* Animated image stage */}
      <div className="relative h-56 overflow-hidden bg-zinc-100 flex items-center justify-center">
        {/* rotating decorative square behind image */}
        <motion.div
          aria-hidden
          className="absolute h-40 w-40 border-2 border-primary/30"
          animate={{ rotate: 360 }}
          transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
        />
        {/* pulsing blue blob */}
        <motion.div
          aria-hidden
          className="absolute h-44 w-44 bg-primary/10 blur-2xl"
          animate={{ scale: [1, 1.25, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: index * 0.5 }}
        />
        {/* floating image */}
        <motion.div
          className="relative z-10 w-[78%] border border-zinc-900 shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] overflow-hidden"
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: index * 0.4 }}
          whileHover={{ scale: 1.06, rotate: -1 }}
        >
          <img src={s.img} alt={s.title} className="w-full h-36 object-cover" />
        </motion.div>
        {/* bobbing icon badge */}
        <motion.div
          className="absolute z-20 top-4 right-4 h-10 w-10 bg-primary flex items-center justify-center"
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: index * 0.3 }}
        >
          <s.icon className="h-5 w-5 text-white" strokeWidth={2} />
        </motion.div>
        {/* sliding tag */}
        <motion.span
          className="absolute z-20 bottom-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-zinc-900 text-white px-2 py-1"
          animate={{ x: [0, 4, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: index * 0.2 }}
        >
          {s.tag}
        </motion.span>
      </div>
      <div className="p-6">
        <h3 className="font-display font-bold text-2xl tracking-tight">{s.title}</h3>
        <p className="mt-2 text-zinc-600 text-sm leading-relaxed">{s.desc}</p>
      </div>
    </motion.div>
  );
}

export default function Landing() {
  return (
    <div className="bg-background" data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">
              Web · SEO · Social
            </span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95]">
              Price your next<br />
              <span className="text-brand-gradient">growth engine</span><br />
              in 60 seconds.
            </h1>
            <p className="mt-6 text-lg text-zinc-600 max-w-xl leading-relaxed">
              Build a transparent, itemized quote for web development, SEO, and social media marketing — then get an AI-crafted proposal instantly.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/build">
                <Button
                  size="lg"
                  data-testid="hero-build-btn"
                  className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest text-sm h-12 px-8"
                >
                  Start Building <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#pricing">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="hero-pricing-btn"
                  className="rounded-none border-zinc-900 font-mono uppercase tracking-widest text-sm h-12 px-8"
                >
                  View Packages
                </Button>
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <motion.div
              className="relative border border-zinc-900 shadow-[8px_8px_0px_0px_rgba(10,10,10,1)]"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              whileHover={{ scale: 1.03, rotate: -0.6, boxShadow: "14px 14px 0px 0px rgba(124,44,191,1)" }}
            >
              <div className="overflow-hidden">
                <HeroSlideshow />
              </div>
              {/* sheen sweep */}
              <motion.div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{ background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)" }}
                animate={{ x: ["-120%", "120%"] }}
                transition={{ duration: 3.2, repeat: Infinity, repeatDelay: 2.5, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute bottom-0 left-0 bg-primary text-white px-5 py-3 font-mono text-xs uppercase tracking-widest flex items-center gap-2"
                initial={{ x: -40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <motion.span
                  className="h-2 w-2 rounded-full bg-green-400 inline-block"
                  animate={{ opacity: [1, 0.2, 1], scale: [1, 1.4, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                />
                Avg. quote ready in &lt; 1 min
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight mb-10">
          One team. Three growth levers.
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <AnimatedServiceCard key={s.title} s={s} index={i} />
          ))}
        </div>
      </section>

      {/* Pricing teaser */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          {[
            { n: "Starter", p: "$1,499.00", f: ["5-page site", "On-page SEO", "1 social profile"], bg: "bg-[#efe3d6]" },
            { n: "Growth", p: "$3,999.00", f: ["12-page custom site", "10-keyword SEO", "3-platform social kit"], pop: true, bg: "bg-blue-50" },
            { n: "Scale", p: "$7,999.00", f: ["Unlimited pages", "25-keyword SEO + ads", "Full social management"], bg: "bg-green-50" },
          ].map((pkg) => (
            <div key={pkg.n} className={`${pkg.bg} p-8 ${pkg.pop ? "ring-2 ring-primary relative z-10" : ""}`}>
              {pkg.pop && (
                <span className="font-mono text-[10px] uppercase tracking-widest bg-primary text-white px-2 py-1">
                  Most Popular
                </span>
              )}
              <h3 className="mt-4 font-display font-bold text-2xl">{pkg.n}</h3>
              <p className="mt-2 font-display font-black text-4xl tracking-tighter">{pkg.p}</p>
              <ul className="mt-6 space-y-2">
                {pkg.f.map((x) => (
                  <li key={x} className="flex items-start gap-2 text-sm text-zinc-700">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {x}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter max-w-2xl leading-tight">
            Stop guessing budgets. Build your exact quote now.
          </h2>
          <Link to="/build">
            <Button
              size="lg"
              data-testid="cta-build-btn"
              className="rounded-none bg-white text-primary hover:bg-zinc-100 font-mono uppercase tracking-widest text-sm h-12 px-8"
            >
              Build a Quote <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-mono text-xs uppercase tracking-widest text-black">
          © {new Date().getFullYear()} Quotient — Web · SEO · Social
        </div>
      </footer>
    </div>
  );
}
